import { UnitInterface, UserInterface, UserVarKey, UserVarsMap } from "@swivl-mono/types";
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoIosInformationCircle } from "react-icons/io";

import { ButtonVariant } from "@swivl-mono/ui";
import { DataCardLocation } from "../../../../Components/DataCards/DataCard.Location";
import Pill, { PillSize, PillStyle, PillType } from "../../../../Components/Pill/Pill";
import { Tooltip } from "../../../../Components/Toolip/Tooltip";
import { filterClasses } from "../../../../Helpers/FilterClasses";
import { useUrlState } from "../../../../Hooks/useUrlState";
import { AlertsModel } from "../../../../Models/Notifications/Alerts.Model";
import { UnitsModel } from "../../../../Models/Units/Units.Model";
import UserVarsModel from "../../../../Models/Variables/UserVars.Model";
import { DataCardFooter } from "../Components/DataCardFooter";
import { FilterDropdown, FilterOption } from "../Components/FilterDropdown";

interface UserDetailsLocationDetailsViewProps {
  navigateForward: (tab?: string) => void;
  user: UserInterface;
  userVars: UserVarsMap | undefined;
}

export function UserDetailsLocationDetailsView({ navigateForward, user, userVars }: UserDetailsLocationDetailsViewProps) {
  const { searchParams, setMultipleSearchParams } = useUrlState();
  const locationId = Number(searchParams.get("locationId"))
  const prevLocationId = useRef(locationId);
  const tableValue = searchParams.get("tableValue");
  const { unitsByLocation } = UnitsModel.useState((s) => s);
  const [visibleTooltipRowId, setVisibleTooltipRowId] = useState<number | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, Set<string>>>({});
  const [sortedUnits, setSortedUnits] = useState<UnitInterface[]>([]);

  const filteredUnits = useMemo(() => {
    if (!locationId || !unitsByLocation?.[locationId]) return [];
    return applyFilter(unitsByLocation[locationId], activeFilters);
  }, [activeFilters, unitsByLocation, locationId, unitsByLocation?.[locationId]]);

  const sizesFilterOptions = useMemo(() => {
    if (!locationId || !unitsByLocation?.[locationId]) return [];
    const sizes = unitsByLocation?.[locationId]?.map((unit) => unit.dimensions) || [];
    const uniqueSizes = Array.from(new Set(sizes));
    return uniqueSizes
      .filter(Boolean)
      .map((size) => ({
        label: size?.toString() ?? "--",
        value: size?.toString() ?? "--",
        disabled: false,
        checked: false,
      }))
      .sort((a, b) => {
        const [aWidth, aHeight] = a.value.split("x").map((dimension) => Number(dimension.trim()));
        const [bWidth, bHeight] = b.value.split("x").map((dimension) => Number(dimension.trim()));
        return aWidth - bWidth || aHeight - bHeight;
      });
  }, [unitsByLocation, locationId, searchParams, unitsByLocation?.[locationId]]);

  const floorFilterOptions = useMemo(() => {
    if (!locationId || !unitsByLocation?.[locationId]) return [];
    const floors = unitsByLocation?.[locationId]?.map((unit) => unit.floor) || [];
    const uniqueFloors = Array.from(new Set(floors));
    return uniqueFloors
      .filter(Boolean)
      .map((floor) => ({
        label: floor?.toString() ?? "--",
        value: floor?.toString() ?? "--",
        disabled: false,
        checked: false,
      }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }, [unitsByLocation, locationId, searchParams, unitsByLocation?.[locationId]]);

  const amenitiesFilterOptions = useMemo(() => {
    if (!locationId || !unitsByLocation?.[locationId]) return [];
    const amenities = unitsByLocation?.[locationId]?.flatMap((unit) => unit.raw.unit_amenities?.map((amenity) => amenity.name)) || [];
    const uniqueAmenities = Array.from(new Set(amenities));
    return uniqueAmenities
      .map((amenity) => ({
        label: amenity?.toString() ?? "--",
        value: amenity?.toString() ?? "--",
        disabled: false,
        checked: false,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [unitsByLocation, locationId, searchParams, unitsByLocation?.[locationId]]);

  const filterOptions = useMemo<FilterOption[]>(() => {
    return [
      {
        id: "sizes",
        label: "Sizes",
        filters: sizesFilterOptions,
      },
      {
        id: "floor",
        label: "Floor",
        filters: floorFilterOptions,
      },
      {
        id: "amenities",
        label: "Amenities",
        filters: amenitiesFilterOptions,
      },
    ];
  }, [amenitiesFilterOptions, sizesFilterOptions, floorFilterOptions, locationId, searchParams]);

  const checkedFilterOptions = useMemo(() => {
    if (!locationId || !filterOptions) return [];

    return filterOptions?.map((option) => ({
      ...option,
      filters: option.filters.map((filter) => ({
        ...filter,
        checked: activeFilters[option.id]?.has(filter.value) ?? false,
      })),
    }));
  }, [activeFilters, locationId, filterOptions]);


  const handleSortColumn = useCallback(
    (tableValue: "size" | "floor" | "price") => {
      const order = searchParams.get("order");
      const newOrder = order === "asc" ? "desc" : "asc";
      setMultipleSearchParams({ order: newOrder, tableValue });

      const sortedUnits = [...(filteredUnits ?? [])].sort((a, b) => {
        switch (tableValue) {
          case "size": {
            const [aWidth, aHeight] = a.dimensions?.split("x").map((dimension) => Number(dimension.trim())) ?? [0, 0];
            const [bWidth, bHeight] = b.dimensions?.split("x").map((dimension) => Number(dimension.trim())) ?? [0, 0];
            if (newOrder === "asc") {
              return aWidth - bWidth || aHeight - bHeight;
            }
            return bWidth - aWidth || bHeight - aHeight;
          }
          case "floor": {
            const aFloor = Number(a.floor);
            const bFloor = Number(b.floor);
            return newOrder === "asc" ? aFloor - bFloor : bFloor - aFloor;
          }
          case "price": {
            const aRate = Number(a.rate);
            const bRate = Number(b.rate);
            return newOrder === "asc" ? aRate - bRate : bRate - aRate;
          }
          default:
            return 0;
        }
      });

      setSortedUnits(sortedUnits);
    },
    [searchParams, filteredUnits, locationId, setMultipleSearchParams],
  );

  useEffect(() => {
    if (locationId && !unitsByLocation?.[locationId]) {
      UnitsModel.getUnitsByLocation(locationId);
    }
  }, [locationId]);

  useEffect(() => {
    setSortedUnits(filteredUnits);
  }, [filteredUnits]);

  // reset filter when locationid changes
  useEffect(() => {
    if (prevLocationId.current !== locationId) {
      setActiveFilters({});
    }
  }, [locationId])

  useEffect(() => {
    setMultipleSearchParams({ tab: undefined, unitId: undefined });
  }, []);

  const handleNavigateForward = (tab: string, unitId: number) => {
    setMultipleSearchParams({
      unitId: String(unitId),
      tab,
    });

    navigateForward(tab);
  };

  function applyFilter(units: UnitInterface[], activeFilters: Record<string, Set<string>>) {
    return units.filter((unit) => {
      for (const [category, selectedValues] of Object.entries(activeFilters)) {
        if (selectedValues.size === 0) continue;
        switch (category) {
          case "sizes":
            if (![...selectedValues].some((value) => unit.dimensions?.includes(value))) return false;
            break;
          case "floor":
            if ([...selectedValues].some((value) => !unit.floor?.toString().includes(value))) return false;
            break;
          case "amenities":
            if ([...selectedValues].some((value) => !unit.raw.unit_amenities.some((amenity) => amenity.name.includes(value)))) {
              return false;
            }
            break;
          default:
            return true;
        }
      }
      return true;
    });
  }

  const handleSaveFilters = ({ optionId, value, checked }: { optionId: string; value: string; checked: boolean }) => {
    setActiveFilters((prevFilter) => {
      const newSet = new Set(prevFilter[optionId] ?? []);
      checked ? newSet.add(value) : newSet.delete(value);
      return { ...prevFilter, [optionId]: newSet };
    });
  };

  const handleResetFilters = () => {
    setActiveFilters({});
  };

  const handleViewFullDetails = () => {
    AlertsModel.errorToast("This feature is not available yet.");
    return;
  };

  const handleSetStore = () => {
    UserVarsModel.saveUserVars(user.id, {
      Location: locationId,
    });
    return;
  };

  // sets tooltip to show for table rows with text overflow
  const handleToggleTooltip = (e: MouseEvent<HTMLTableRowElement>, unitId: number | null) => {
    const row = e.currentTarget;
    const cel = row.querySelector(".check-overflow");
    if (cel && cel.scrollWidth > cel.clientWidth) {
      setVisibleTooltipRowId(unitId);
    } else {
      setVisibleTooltipRowId(null);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="mb-4">
        <DataCardLocation
          title="Prospect Profile"
          locationId={locationId}
          pills={
            Number(userVars?.[UserVarKey.Location]) === locationId && [
              <Pill type={PillType.ORANGE} size={PillSize.SMALL} style={PillStyle.DARK} text="Selected"></Pill>,
            ]
          }
          size="full"
          footer={
            <DataCardFooter
              handleSetStore={handleSetStore}
              handleViewFullDetails={handleViewFullDetails}
              locationId={locationId}
              userVars={userVars}
            />
          }
        />
      </div>

      <div className="flex items-center px-1 justify-between">
        <h2 className="text-left py-2 text-black font-sans font-semibold text-[15px]">Available Units</h2>
        <FilterDropdown
          id="unit-filters"
          filterOptions={checkedFilterOptions}
          position="right"
          resetFilters={handleResetFilters}
          saveFilters={handleSaveFilters}
          variant={ButtonVariant.GHOST}
        />
      </div>
      <div className="rounded-md overflow-hidden">
        <div className="bg-grey-100 flex flex-col space-y-6 max-h-[calc(100vh-460px)] border-grey-200 border-2 overflow-auto [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-grey-400 [&::-webkit-scrollbar-thumb]:rounded-md">
          <table className="w-full table-auto">
            <thead className="sticky top-0 z-10 bg-grey-100">
              <tr>
                <th
                  className={filterClasses(tableValue === "size" && "underline", "cursor-pointer pl-3 py-3")}
                  onClick={() => handleSortColumn("size")}
                  scope="col"
                >
                  <h2 className="text-black font-sans font-semibold text-[15px]">Size</h2>
                </th>
                <th
                  className={filterClasses(tableValue === "floor" && "underline", "cursor-pointer px-2.5 py-3")}
                  onClick={() => handleSortColumn("floor")}
                  scope="col"
                >
                  <h2 className="text-black font-sans font-semibold text-[15px]">Floor</h2>
                </th>
                <th
                  scope="col"
                  className={filterClasses(tableValue === "price" && "underline", "cursor-pointer px-2.5 py-3")}
                  onClick={() => handleSortColumn("price")}
                >
                  <h2 className="text-black font-sans font-semibold text-[15px]">Price</h2>
                </th>
                <th className="py-3 px-2.5" scope="col">
                  <h2 className="text-black font-sans font-semibold text-[15px]">Amenities</h2>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y-2 divide-grey-200">
              {sortedUnits?.length > 0 &&
                sortedUnits.map((unit) => (
                  <tr
                    aria-label={`Unit ${unit.id} details`}
                    className="hover:bg-orange-50 relative bg-white hover:text-orange-500 transition-colors duration-200 cursor-pointer"
                    onClick={() => handleNavigateForward("Unit Details", unit.id)}
                    onMouseEnter={(e) => handleToggleTooltip(e, unit.id)}
                    onMouseLeave={(e) => handleToggleTooltip(e, null)}
                    key={unit.id}
                  >
                    <td className="pl-3 py-2">{unit?.dimensions ?? "--"}</td>
                    <td className="px-2.5 py-2">{unit?.floor ? `Floor ${unit.floor}` : "--"}</td>
                    <td className="px-2.5 py-2">{unit?.rate ? `$${unit?.rate}` : "--"}</td>
                    <td className="check-overflow pl-2.5 pr-20 py-2 whitespace-nowrap max-w-xs text-ellipsis overflow-hidden">
                      {unit?.raw.unit_amenities?.length > 0
                        ? unit.raw.unit_amenities.map((amenity, index) =>
                          index === unit.raw.unit_amenities.length - 1 ? amenity.name : `${amenity.name}, `,
                        )
                        : "--"}
                      <Tooltip
                        content={unit?.raw.unit_amenities?.length > 0 ? unit.raw.unit_amenities.map((amenity) => amenity.name).join(", ") : "--"}
                        className={filterClasses(visibleTooltipRowId === unit.id ? "absolute right-4 top-2.5" : "hidden")}
                        placement="top-end"
                      >
                        <IoIosInformationCircle className="fill-black size-5" />
                      </Tooltip>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
